import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Howler } from "howler";

export const AppActivityContext = createContext(null);

/**
 * AppActivityProvider — Production-grade tab visibility management
 *
 * Provides `isActive` state that tracks whether the browser tab is visible.
 * When the tab is hidden:
 *   - Audio is muted via Howler
 *   - Pixi ticker speed is set to 0 (freezes rendering without killing the loop)
 *
 * Design decisions:
 *   1. Uses ONLY `visibilitychange` — `blur`/`focus` cause false positives
 *      (DevTools, alerts, iframes). `pagehide`/`freeze`/`pageshow` are redundant
 *      as they always fire alongside or after `visibilitychange`.
 *   2. Uses `ticker.speed = 0` instead of `ticker.stop()` — stop() kills the
 *      ticker loop entirely, causing a delta time spike on restart that corrupts
 *      spine animations and tweens. speed=0 keeps the loop alive but produces
 *      deltaTime=0 for all subscribers, so nothing visually progresses.
 *   3. 100ms debounce prevents rapid state flicker from quick tab switching
 *      or edge cases where visibility events fire multiple times.
 */
export const AppActivityProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    let debounceTimer = null;

    const handleVisibilityChange = () => {
      const active = document.visibilityState === "visible";

      // Debounce: prevent rapid state flicker from quick tab switching
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        setIsActive((prev) => {
          if (prev === active) return prev;

          // Mute/unmute audio
          Howler.mute(!active);

          // Freeze/unfreeze Pixi ticker without killing the loop
          // ticker.speed = 0 produces deltaTime = 0 for all subscribers,
          // so nothing visually progresses. When speed goes back to 1,
          // there is no accumulated delta spike — clean resume.
          const app = globalThis.__PIXI_APP__;
          if (app && app.ticker) {
            app.ticker.speed = active ? 1 : 0;
          }

          return active;
        });
      }, 100);
    };

    // Initialize with current state
    handleVisibilityChange();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);

  // Stable context value — only re-creates when isActive changes
  const value = useMemo(
    () => ({
      isActive,
    }),
    [isActive]
  );

  return (
    <AppActivityContext.Provider value={value}>
      {children}
    </AppActivityContext.Provider>
  );
};

// Hook for consuming the context
export const useAppActivity = () => {
  const context = useContext(AppActivityContext);
  if (!context) {
    throw new Error(
      "useAppActivity must be used within an AppActivityProvider"
    );
  }
  return context;
};
